import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DraftMode, MailAction, MailAttachment, MailListItem, MailSort, MailView, ProcessedFilter } from '@jerome2/shared';
import { api } from '../api.ts';
import { useShortcuts } from '../lib/keys.ts';
import { useLayer } from '../lib/layers.ts';
import { useStore } from '../lib/store.ts';
import { app, setPhoneDetail } from '../state/app.ts';
import { act, emptyList, forgetMail, instruct, isReadyItem, listKey, loadDetail, loadList, mailStore, markEod, markStale, matchesSel, nextIds, nextItems, prefetch, readyItems, refreshAll, unmarkEod, viewLeftBy, visibleItems, watchDetail, type Sel } from '../state/mail.ts';
import { latestTaskForMessage, loadTask, markSeen, openSiblings, taskActions, tasksStore } from '../state/tasks.ts';
import { updateTabState, type Tab } from '../state/tabs.ts';
import { errorToast, toast } from '../state/toast.ts';
import { primeScribeToken } from '../voice/scribe.ts';
import { FolderRail } from './FolderRail.tsx';
import { FilterBar, type Filters } from './FilterBar.tsx';
import { MailList } from './MailList.tsx';
import { ContextMenu, mailMenuItems } from './ContextMenu.tsx';
import { Reader, ReaderSkeleton } from './Reader.tsx';
import { PhoneReaderBar } from './PhoneReaderBar.tsx';
import { ActionSheet } from './ActionSheet.tsx';
import { ResultCard } from './ResultCard.tsx';
import { InstructionBar, submitsDirectly, type BarMode, type BarSource, type InstructionBarHandle } from './InstructionBar.tsx';
import { Editor } from './Editor.tsx';
import { AttachmentViewer } from './AttachmentViewer.tsx';
import { MailLayout } from './MailLayout.tsx';
import { setMailChoices, useMailChoices, viewMenuItems } from './readerLayout.ts';
import { useMailRefresh } from './refresh.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, Empty } from '../ui/primitives.tsx';
import { Sheet } from '../ui/Sheet.tsx';

// Het mailscherm: mappen, filterbalk, lijst, lezer (met resultaatkaart bij Verwerkt), editor,
// bijlageviewer, rechtsklikmenu en de instructiebalk. Toetsen: j/k, n, Enter, 1-6, r/a/f, e, #,
// s, u, d (einde dag), m, Esc. Na vlag weg in Gevlagd, gelezen in Ongelezen of Klaar gaat de
// selectie door naar de volgende mail. Bij Verwerkt is elk item een taak (een mail met meerdere
// acties staat er meerdere keren); de selectie kent daar naast de mail ook de taak. Elke
// instructie in de balk is een nieuwe taak; bijsturen alleen via de knop Stuur bij. In Gevlagd en
// Ongelezen staat bovenaan het blok "Klaar voor jou": de taken met een resultaat of een vraag,
// zodat Jeroen op één pagina kan blijven werken (klik = mail met resultaatkaart). Op de telefoon:
// lijst óf lezer (detailstand, de terugbeweging sluit de mail) en de mappen in een vel; editor en
// bijlageviewer zijn daar eigen lagen (de terugbeweging sluit eerst die). De instructiebalk hoort bij
// één mail en taak (key): tekst en stand gaan niet mee naar een andere mail. De editor onthoudt bij
// het openen om welke mail het gaat, ook als de selectie daarna doorloopt.
// Op de desktop (ticket 28625) staat de lezer rechts van de lijst of eronder, zoals in Outlook (het menu Weergave), met een
// greep op de rand ertussen, en klapt de lijst met de knop Lezer vergroten, het menu Weergave of de knop op de strook in tot een
// strook van 44 px, zodat de lezer de rest krijgt; zonder open mail staat de lijst er altijd (readerLayout.ts). De indeling zelf
// (rij of kolom, metingen, grenzen, grepen en strook) staat in MailLayout.tsx; dit scherm geeft de lijst en de lezer als inhoud.

const VoiceOverlay = lazy(() => import('../voice/VoiceOverlay.tsx').then((m) => ({ default: m.VoiceOverlay })));
const Dictation = lazy(() => import('../voice/Dictation.tsx').then((m) => ({ default: m.Dictation })));

interface EditorState {
  mode?: DraftMode | undefined;
  draftId?: string | undefined;
  /** De mail waar het concept bij hoort, vastgelegd bij het openen. */
  messageId: string;
}

interface MailTabState {
  view?: MailView;
  folder?: string;
  sort?: MailSort;
  processed?: ProcessedFilter;
  q?: string;
  selectedId?: string | null;
  /** De gekozen taak van de gekozen mail (Verwerkt, of een item onder "Klaar voor jou"; de werkerskolom geeft hem ook mee). */
  taskId?: string | null;
  foldersOpen?: boolean;
  /** Het blok "Klaar voor jou" boven Gevlagd en Ongelezen open of ingeklapt. */
  readyOpen?: boolean;
}

export function MailScreen(props: { tab: Tab; voice: boolean; onVoiceEnd: () => void }) {
  const settings = useStore(app, (s) => s.settings);
  const narrow = useStore(app, (s) => s.narrow);
  const ts = props.tab.state as MailTabState;
  const filters: Filters = useMemo(() => ({ view: ts.view ?? 'flagged', folder: ts.folder ?? 'all', sort: ts.sort ?? settings.defaultSort, processed: ts.processed ?? 'all', q: ts.q ?? '' }), [ts.view, ts.folder, ts.sort, ts.processed, ts.q, settings.defaultSort]);
  const key = listKey(filters);
  const list = useStore(mailStore, (s) => s.lists[key]) ?? emptyList();
  const visible = useMemo(() => visibleItems(list.items, filters.view), [list.items, filters.view]);
  const selectedId = ts.selectedId ?? null;
  const selectedTaskId = ts.taskId ?? null;
  const sel: Sel | null = useMemo(() => (selectedId ? { id: selectedId, taskId: selectedTaskId } : null), [selectedId, selectedTaskId]);
  // "Klaar voor jou": taken met een resultaat of een vraag, bovenaan in Gevlagd en Ongelezen; samen met de mails één lijst voor j/k en Volgende.
  const byId = useStore(tasksStore, (s) => s.byId);
  const ready = useMemo(() => (filters.view === 'flagged' || filters.view === 'unread' ? readyItems(byId) : []), [byId, filters.view]);
  const [readyOpen, setReadyOpen] = useState(ts.readyOpen ?? true);
  const combined = useMemo(() => (ready.length && readyOpen ? [...ready, ...visible] : visible), [ready, readyOpen, visible]);
  const detailEntry = useStore(mailStore, (s) => (selectedId ? s.details[selectedId] : undefined));
  const detailError = useStore(mailStore, (s) => (selectedId ? s.detailErrors[selectedId] : undefined));
  const detail = detailEntry?.detail ?? null;
  // De taak bij de selectie: bij Verwerkt de gekozen taak, elders de nieuwste (actieve) taak van de mail.
  const task = useStore(tasksStore, (s) => (selectedId ? ((selectedTaskId ? s.byId[selectedTaskId] : undefined) ?? latestTaskForMessage(s, selectedId)) : null));
  const siblings = useStore(tasksStore, (s) => (task ? openSiblings(s, task.messageId, task.id) : 0));
  const [steering, setSteering] = useState(false);
  // De lezer begint bovenaan bij elke nieuwe keuze (mail of taak), zodat de resultaatkaart in beeld staat.
  const readerScroll = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (readerScroll.current) readerScroll.current.scrollTop = 0;
  }, [selectedId, selectedTaskId]);
  // Mappenkolom (desktop) uit de tabstaat; op de telefoon is het een vel dat alleen even open staat.
  const foldersOpen = !narrow && (ts.foldersOpen ?? true);
  const [folderSheet, setFolderSheet] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [viewer, setViewer] = useState<MailAttachment | null>(null);
  // Dicteren in de balk: null is uit; anders de tekst die al in het veld stond toen de microfoon aanging.
  const [dictating, setDictating] = useState<string | null>(null);
  // Het actiemenu: rechtsklik of lang indrukken op een rij, of Meer in de lezerkop op de telefoon (`from: 'reader'`).
  const [menu, setMenu] = useState<{ id: string; x: number; y: number; from?: 'reader' } | null>(null);
  const folders = useStore(mailStore, (s) => s.folders);
  const bar = useRef<InstructionBarHandle>(null);
  const tabId = props.tab.id;
  // De indeling (desktop, MailLayout.tsx): de lezer rechts van de lijst of eronder, en met een open mail en de keuze uit de lijst
  // ingeklapt tot de strook met het aantal ongelezen mails. De mail mag 1200 px breed als de lezer de volle breedte
  // heeft: met de lijst ingeklapt, of onder de lijst.
  const choices = useMailChoices();
  const listVisible = choices.listVisible;
  const setListVisible = (visible: boolean) => setMailChoices({ listVisible: visible });
  const below = !narrow && choices.readerPosition === 'below';
  const collapsed = !narrow && !listVisible && Boolean(selectedId);
  const wide = collapsed || below;
  // Het aantal op de strook: alle ongelezen mail volgens de server, dezelfde teller als Ongelezen in de filterbalk (en Outlook),
  // niet alleen de ongelezen mails in de geladen lijst; null zolang de tellers er nog niet zijn.
  const unread = useStore(tasksStore, (s) => (s.countsLoaded ? s.counts.unread : null));
  // De selectie van dit moment, voor werk dat na een wachttijd nog moet weten of de keuze intussen veranderd is.
  const selRef = useRef<Sel | null>(sel);
  selRef.current = sel;

  const patch = useCallback((p: Partial<Filters & MailTabState>) => updateTabState(tabId, p), [tabId]);
  const select = useCallback((id: string | null, taskId: string | null = null) => updateTabState(tabId, { selectedId: id, taskId }), [tabId]);
  /** Opent de editor voor een mail (standaard de gekozen); de mail staat vast, ook als de selectie daarna doorloopt. */
  const openEditor = useCallback(
    (e: { mode?: DraftMode | undefined; draftId?: string | undefined }, messageId: string | null = selectedId) => {
      if (messageId) setEditor({ ...e, messageId });
    },
    [selectedId],
  );
  /** Kiest een lijstitem; bij Verwerkt en bij een item onder "Klaar voor jou" hoort daar de taak van dat item bij. */
  const selectItem = useCallback((item: MailListItem | null | undefined) => select(item ? item.id : null, item && item.task && (filters.view === 'processed' || isReadyItem(item)) ? item.task.id : null), [select, filters.view]);
  // Bekeken (2.3.10, ticket 37198): kiest de eigenaar zelf een item met een taak (een klik in de lijst of onder Klaar voor jou, j en k,
  // Volgende, Openen in het menu), dan opent hij het resultaat. De keuze die de app zelf maakt (het eerste item, of de mail die na een
  // actie volgt) zet niets: dan heeft hij het resultaat niet geopend. `markSeen` doet niets bij een taak die bezig is of al bekeken is.
  const seeTask = useCallback((taskId: string | null) => {
    const t = taskId ? tasksStore.get().byId[taskId] : undefined;
    if (t) void markSeen(t);
  }, []);
  const pickByOwner = useCallback((id: string | null, taskId: string | null = null) => {
    select(id, taskId);
    seeTask(taskId);
  }, [select, seeTask]);
  const pickItemByOwner = useCallback((item: MailListItem | null | undefined) => {
    selectItem(item);
    seeTask(item && item.task && (filters.view === 'processed' || isReadyItem(item)) ? item.task.id : null);
  }, [selectItem, seeTask, filters.view]);
  const nextAfter = useCallback((from: Sel | string | null) => nextItems(combined, from, 1, filters.view)[0] ?? null, [combined, filters.view]);

  // Lijst laden en verversen.
  useEffect(() => {
    void loadList(filters);
  }, [filters, key]);
  useEffect(() => {
    if (list.stale && !list.loading) void loadList(filters, { force: true, background: true });
  }, [list.stale, list.loading, filters]);
  // Wat in Outlook verandert (vlag weg, gelezen, nieuwe vlag): met de stroom van de maildienst komt dat als melding (mail.changed, 2.3.2)
  // en is dit het vangnet van tien minuten; zonder stroom elke minuut zolang het venster zichtbaar is, en bij terugkeer naar het venster,
  // de lijst, de mappen met tellers en de tellers bovenin verversen (mail/refresh.ts).
  useMailRefresh(useCallback(() => void refreshAll(filters).catch(() => undefined), [filters]));

  // Selectie bijhouden: eerste item als er niets gekozen is (bij Verwerkt: dezelfde mail met een andere taak
  // als de gekozen taak uit de lijst is), en vooruit ophalen.
  useEffect(() => {
    if (!combined.length) return;
    if (!sel || !combined.some((i) => matchesSel(i, sel, filters.view))) {
      const sameMail = sel ? combined.find((i) => i.id === sel.id) : undefined;
      const pick = sameMail ?? (narrow ? undefined : combined[0]);
      if (pick) selectItem(pick);
    }
  }, [combined, sel, selectItem, narrow, filters.view]);
  useEffect(() => {
    if (!selectedId) return;
    void loadDetail(selectedId).catch(() => undefined);
    prefetch(nextIds(combined, sel, settings.prefetch, filters.view));
  }, [selectedId, sel, combined, settings.prefetch, filters.view]);
  // De open mail volgt wat er in Outlook verandert (vlag, gelezen, nieuwe taak): een stille verversing bij een melding.
  useEffect(() => (selectedId ? watchDetail(selectedId) : undefined), [selectedId]);
  // Een gekozen taak die de app nog niet kent (koppeling, ouder dan vandaag): ophalen.
  useEffect(() => {
    if (selectedTaskId && !tasksStore.get().byId[selectedTaskId]) void loadTask(selectedTaskId).catch(() => undefined);
  }, [selectedTaskId]);
  // Bijsturen geldt voor de gekozen taak; een andere keuze zet de balk terug op een nieuwe instructie.
  useEffect(() => setSteering(false), [selectedId, selectedTaskId]);
  // Telefoon: een open mail is de detailstand (navigatie onderin weg) en een laag voor de terugbeweging.
  // Een open mail en de spraakmodus zijn op de telefoon de detailstand (navigatie onderin weg) en samen één laag:
  // de terugbeweging sluit de spraakmodus én gaat terug naar de lijst; Stop laat de mail open staan.
  const voice = props.voice;
  useEffect(() => {
    if (!narrow) return;
    setPhoneDetail(Boolean(selectedId) || voice);
    return () => setPhoneDetail(false);
  }, [narrow, selectedId, voice]);
  useLayer(narrow && (Boolean(selectedId) || voice), () => {
    if (voice) props.onVoiceEnd();
    select(null);
  });
  // Editor en bijlageviewer liggen op de telefoon boven de mail: de terugbeweging sluit eerst die (de editor bewaart wat nog vuil is).
  useLayer(narrow && editor !== null, () => setEditor(null));
  useLayer(narrow && viewer !== null, () => setViewer(null));
  // Een Scribe-token klaarleggen (en elke tien minuten vernieuwen), zodat de microfoon direct start.
  useEffect(() => {
    primeScribeToken();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') primeScribeToken();
    }, 10 * 60_000);
    return () => clearInterval(timer);
  }, []);

  const goNext = useCallback(() => {
    const next = nextAfter(sel);
    if (next) pickItemByOwner(next);
    else toast('Dit was de laatste in de lijst', { tone: 'info' });
  }, [sel, nextAfter, pickItemByOwner]);
  const goPrev = useCallback(() => {
    const idx = sel ? combined.findIndex((i) => matchesSel(i, sel, filters.view)) : -1;
    const prev = combined[Math.max(0, idx - 1)];
    if (prev) pickItemByOwner(prev);
  }, [combined, sel, pickItemByOwner, filters.view]);

  // Een vraag van Jerome vraagt om een antwoord; bijsturen alleen na een klik op Stuur bij; anders is elke instructie een nieuwe taak.
  const barMode: BarMode = !selectedId ? 'none' : task && task.status === 'question' ? 'answer' : steering && task && task.status !== 'finished' && task.status !== 'cancelled' ? 'steer' : 'instruct';

  const submit = useCallback(
    async (text: string, source: BarSource) => {
      if (!selectedId) return;
      try {
        if (barMode === 'answer' && task) {
          await taskActions.answer(task.id, text);
          toast('Antwoord doorgegeven; Jerome gaat verder', { tone: 'ok' });
        } else if (barMode === 'steer' && task && task.kind === 'sign' && task.status === 'done') {
          const rule = await taskActions.signFeedback(task.id, text);
          toast(`Leerregel bewaard: ${rule.text}`, { tone: 'ok' });
          setSteering(false);
        } else if (barMode === 'steer' && task) {
          await taskActions.steer(task.id, text);
          toast('Bijgestuurd', { tone: 'ok' });
          setSteering(false);
        } else {
          const next = nextAfter(sel);
          await instruct(selectedId, { text, source });
          toast('Naar de wachtrij', { tone: 'ok' });
          // Door naar de volgende, tenzij Jeroen intussen zelf al een andere mail koos.
          if ((filters.view === 'flagged' || filters.view === 'unread') && selRef.current?.id === selectedId) selectItem(next);
        }
      } catch (e) {
        errorToast(e);
        throw e;
      }
    },
    [selectedId, sel, barMode, task, filters.view, nextAfter, selectItem],
  );

  const onAction = useCallback(
    async (action: MailAction, folderId?: string, folderName?: string, id?: string) => {
      const target = id ?? selectedId;
      if (!target) return;
      const next = nextAfter(target === selectedId ? sel : target);
      // Verdwijnt de mail uit deze weergave, kies dan vóór de actie al de volgende (anders springt de selectie eerst naar de eerste).
      const gone = action === 'archive' || action === 'delete' || action === 'move' || viewLeftBy(action) === filters.view;
      if (gone && target === selectedId) selectItem(next);
      try {
        await act(target, action, { ...(folderId ? { folderId } : {}), ...(folderName ? { folderName } : {}) });
      } catch (e) {
        if (gone && target === selectedId) select(target, selectedTaskId);
        errorToast(e);
      }
    },
    [selectedId, selectedTaskId, sel, select, selectItem, nextAfter, filters.view],
  );

  // Naar (of uit) de einde-dag-samenvatting; de mail krijgt een taak en verdwijnt dan uit Gevlagd/Ongelezen.
  const onEod = useCallback(
    async (mark: boolean, id?: string) => {
      const target = id ?? selectedId;
      if (!target) return;
      const next = nextAfter(target === selectedId ? sel : target);
      const leaves = mark && (filters.view === 'flagged' || filters.view === 'unread') && target === selectedId;
      if (leaves) selectItem(next);
      try {
        if (mark) await markEod(target);
        else await unmarkEod(target);
      } catch (e) {
        if (leaves) select(target, selectedTaskId);
        errorToast(e);
      }
    },
    [selectedId, selectedTaskId, sel, select, selectItem, nextAfter, filters.view],
  );

  // Een concept direct verzenden (vanuit de balk of het rechtsklikmenu): daarna is het weg uit Concepten en gaat de selectie door.
  const sendDraft = useCallback(
    async (id: string) => {
      const next = nextAfter(id === selectedId ? sel : id);
      try {
        const r = await api<{ subject: string; learning: boolean }>(`/api/drafts/${encodeURIComponent(id)}/send`, { method: 'POST' });
        toast(`Verzonden: ${r.subject}`, { tone: 'ok' });
        if (r.learning) toast('Jerome bekijkt wat je aan zijn concept veranderde en leert daarvan', { tone: 'info' });
        forgetMail(id);
        markStale();
        if (id === selectedId) selectItem(next);
      } catch (e) {
        errorToast(e);
      }
    },
    [selectedId, sel, selectItem, nextAfter],
  );

  // Een map kiezen houdt Gevlagd of Ongelezen vast (beperkt tot die map); Concepten en Verzonden tonen altijd de hele map.
  const viewForFolder = useCallback(
    (id: string): MailView => {
      if (id === 'all') return filters.view === 'folder' ? 'flagged' : filters.view;
      const wellKnown = folders.find((f) => f.id === id)?.wellKnown;
      if (wellKnown === 'drafts' || wellKnown === 'sentitems') return 'folder';
      return filters.view === 'flagged' || filters.view === 'unread' ? filters.view : 'folder';
    },
    [filters.view, folders],
  );

  const menuTarget = menu ? (combined.find((i) => i.id === menu.id) ?? (detail && detail.id === menu.id ? detail : null)) : null;
  const menuItems = menu && menuTarget
    ? mailMenuItems(menuTarget, folders, {
        open: () => pickItemByOwner(menuTarget),
        compose: (mode) => {
          selectItem(menuTarget);
          openEditor({ mode }, menuTarget.id);
        },
        action: (a, f, n) => void onAction(a, f, n, menu.id),
        eod: (mark) => void onEod(mark, menu.id),
        instruct: () => {
          selectItem(menuTarget);
          setTimeout(() => bar.current?.focus(), 50);
        },
        edit: () => {
          selectItem(menuTarget);
          openEditor({ draftId: menu.id }, menuTarget.id);
        },
        // Vanuit het menu is er geen tweede klik; de bevestiging zit in de melding.
        send: () => {
          const subject = menuTarget.subject || '(geen onderwerp)';
          toast(`"${subject}" verzenden?`, { tone: 'warn', ms: 10000, action: { label: 'Ja, verzenden', run: () => sendDraft(menu.id) } });
        },
      })
    : [];
  // In het vel vanuit de lezer is "Openen" overbodig (de mail staat al open), net als de scheiding erna.
  const sheetItems = (() => {
    if (menu?.from !== 'reader') return menuItems;
    const rest = menuItems.filter((e) => e === 'separator' || e.id !== 'open');
    return rest[0] === 'separator' ? rest.slice(1) : rest;
  })();

  useShortcuts(
    (e) => {
      if (editor || viewer || props.voice) return false;
      const k = e.key;
      if (k === 'j' || k === 'ArrowDown') return (goNext(), true);
      if (k === 'k' || k === 'ArrowUp') return (goPrev(), true);
      if (k === 'n') return (goNext(), true);
      if (k === 'Enter') return (bar.current?.focus(), true);
      if (/^[1-9]$/.test(k)) {
        const q = settings.quickInstructions.find((x) => x.key === k);
        if (q && barMode !== 'none') {
          // Alleen een nieuwe instructie gaat direct weg; bij bijsturen of een vraag komt de tekst in het veld.
          bar.current?.insert(q.text, barMode === 'instruct' && submitsDirectly(q.text));
          return true;
        }
        return false;
      }
      if (!selectedId) return false;
      if (k === 'r') return (openEditor({ mode: 'reply' }), true);
      if (k === 'a') return (openEditor({ mode: 'replyAll' }), true);
      if (k === 'f') return (openEditor({ mode: 'forward' }), true);
      if (k === 'e') return (void onAction('archive'), true);
      if (k === '#') return (void onAction('delete'), true);
      if (k === 's') return (void onAction(detail?.flagged ? 'unflag' : 'flag'), true);
      if (k === 'u') return (void onAction(detail?.isRead ? 'unread' : 'read'), true);
      if (k === 'd') return (void onEod(!detail?.eodDay), true);
      return false;
    },
    [editor, viewer, props.voice, goNext, goPrev, selectedId, detail, onAction, onEod, openEditor, settings.quickInstructions, barMode],
  );

  // De lijst met Klaar voor jou erboven; MailLayout zet haar naast of boven de lezer, met de breedte of de hoogte.
  const listPane = (
    <>
      {ready.length ? (
        <section className={`shrink-0 flex flex-col border-b-2 border-line-strong bg-paper/40 ${readyOpen ? 'max-h-[45%]' : ''}`} aria-label="Klaar voor jou">
          <button
            type="button"
            aria-expanded={readyOpen}
            onClick={() => {
              setReadyOpen(!readyOpen);
              patch({ readyOpen: !readyOpen });
            }}
            className="shrink-0 h-9 px-4 flex items-center gap-2 whitespace-nowrap text-[13px] font-medium text-ink-soft hover:bg-paper"
          >
            {/* Op één regel, ook bij een smal ingestelde lijst (ticket 28625): alleen de omschrijving rechts wordt afgekapt. */}
            <Icon name="chevron" size={13} className={readyOpen ? 'rotate-90' : ''} />
            Klaar voor jou
            <span className="inline-flex items-center justify-center rounded-full bg-accent text-white text-[11px] h-5 min-w-5 px-1.5">{ready.length}</span>
            <span className="ml-auto font-normal text-muted truncate">resultaten en vragen van Jerome</span>
          </button>
          {readyOpen ? (
            <div className="flex-1 min-h-0">
              <MailList items={ready} view="processed" label="Klaar voor jou" selectedId={selectedId} selectedTaskId={selectedTaskId} onSelect={pickByOwner} loading={false} error={null} more={false} onMore={() => undefined} onRetry={() => undefined} showUrgency={false} onContextMenu={(id, x, y) => setMenu({ id, x, y })} />
            </div>
          ) : null}
        </section>
      ) : null}
      <div className="flex-1 min-h-0">
        <MailList items={visible} view={filters.view} selectedId={selectedId} selectedTaskId={selectedTaskId} onSelect={pickByOwner} loading={list.loading} error={list.error} more={list.more} onMore={() => void loadList(filters, { more: true })} onRetry={() => void loadList(filters, { force: true })} showUrgency={filters.sort === 'urgent'} onContextMenu={(id, x, y) => setMenu({ id, x, y })} onFlag={(id, flagged) => void onAction(flagged ? 'flag' : 'unflag', undefined, undefined, id)} />
      </div>
    </>
  );

  // De resultaatkaart: bij Verwerkt altijd, elders als de gekozen taak op Jeroen wacht (item onder "Klaar voor jou" of via de werkerskolom).
  const cardTask = task && (filters.view === 'processed' || (selectedTaskId !== null && task.id === selectedTaskId)) ? task : null;

  // De lezer met de instructiebalk eronder; de editor en de bijlageviewer liggen erover (MailLayout geeft het vak, relatief).
  const readerPane = (
    <>
      {narrow && selectedId ? (
        <PhoneReaderBar flagged={Boolean(detail?.flagged)} isDraft={detail?.isDraft} onBack={() => select(null)} onReply={() => openEditor({ mode: 'reply' })} onArchive={() => void onAction('archive')} onFlag={() => void onAction(detail?.flagged ? 'unflag' : 'flag')} onMore={(x, y) => (detail ? setMenu({ id: detail.id, x, y, from: 'reader' }) : undefined)} />
      ) : null}
      <div ref={readerScroll} className="flex-1 min-h-0 overflow-auto scroll-thin">
        {!selectedId ? (
          <Empty title={filters.view === 'processed' ? 'Kies een verwerkte mail' : 'Kies een mail'} text="Of gebruik j en k om door de lijst te lopen." icon="mail" />
        ) : detail ? (
          <>
            {cardTask ? (
              <ResultCard
                key={cardTask.id}
                task={cardTask}
                lastForMail={siblings === 0}
                onEdit={(draftId) => openEditor({ mode: (cardTask.result?.draftMode ?? 'reply') as DraftMode, draftId }, cardTask.messageId)}
                onSteer={() => {
                  setSteering(true);
                  setTimeout(() => bar.current?.focus(), 0);
                }}
                onAnswer={() => bar.current?.focus()}
                onFinished={() => selectItem(nextAfter(sel))}
              />
            ) : null}
            {task && !cardTask && task.status !== 'finished' && task.status !== 'cancelled' ? (
              <div className={`${narrow ? 'mx-3' : 'mx-6'} mt-4 flex flex-wrap items-center gap-2 rounded-lg bg-tint px-3 py-2 text-[13px] text-ink-soft`}>
                <Icon name="bolt" size={14} className="text-accent" />
                Jerome is hiermee bezig ({task.status === 'done' ? 'klaar' : task.status === 'question' ? 'vraag' : task.status}).
                <Button size="sm" variant="ghost" onClick={() => patch({ view: 'processed', processed: 'all' })}>
                  Naar Verwerkt
                </Button>
              </div>
            ) : null}
            <Reader detail={detail} compact={narrow} wide={wide} enlarge={narrow ? undefined : { enlarged: collapsed, onToggle: () => setListVisible(collapsed) }} onAction={(a, f, n) => void onAction(a, f, n)} onCompose={(mode) => openEditor({ mode }, detail.id)} onOpenAttachment={setViewer} onEod={(mark) => void onEod(mark)} onContextMenu={(x, y) => setMenu({ id: detail.id, x, y })} onEditDraft={(draftId) => openEditor({ draftId }, detail.id)} onSendDraft={sendDraft} onInstruct={() => bar.current?.focus()} />
          </>
        ) : detailError ? (
          <div className="p-8 text-center">
            <p className="text-red mb-3">{detailError}</p>
            <Button onClick={() => void loadDetail(selectedId, { force: true }).catch(() => undefined)} icon="refresh">Opnieuw</Button>
          </div>
        ) : (
          <ReaderSkeleton wide={wide} />
        )}
      </div>
      {/* De balk hoort bij deze mail en taak: een andere keuze begint leeg. De dicteerpil zweeft erboven. */}
      <div className="relative shrink-0">
        <InstructionBar
          key={`${selectedId ?? ''}#${task?.id ?? ''}`}
          ref={bar}
          mode={barMode}
          quick={settings.quickInstructions}
          onSubmit={submit}
          onNext={goNext}
          hasNext={nextAfter(sel) !== null}
          question={task?.question}
          steerOf={barMode === 'steer' ? task?.instruction : null}
          onCancelSteer={() => setSteering(false)}
          onMic={() => setDictating((d) => (d === null ? (bar.current?.getValue() ?? '') : null))}
          listening={dictating !== null}
        />
        {dictating !== null ? (
          <Suspense fallback={null}>
            <Dictation
              initial={dictating}
              onPartial={(t) => bar.current?.setValue(t)}
              onFinal={(t, submitNow) => {
                // Een afgesloten segment komt in het veld en het luisteren gaat door; "oké" stopt en verstuurt.
                if (!submitNow) return bar.current?.setValue(t);
                setDictating(null);
                bar.current?.insert(t, true, 'voice');
              }}
              onStop={() => setDictating(null)}
              vocabulary={settings.voice.vocabulary}
            />
          </Suspense>
        ) : null}
      </div>
      {editor ? (
        <Editor
          mode={editor.mode}
          messageId={editor.messageId}
          draftId={editor.draftId}
          onClose={(r) => {
            setEditor(null);
            if (r.sent) {
              // Een verstuurd concept is weg uit Concepten; een beantwoorde mail krijgt een verse lijst. Door naar de volgende.
              const next = nextAfter(sel);
              if (editor.draftId) forgetMail(editor.draftId);
              markStale();
              if (editor.draftId) selectItem(next);
            }
          }}
        />
      ) : null}
      {viewer && selectedId ? <AttachmentViewer messageId={selectedId} attachment={viewer} onClose={() => setViewer(null)} /> : null}
    </>
  );

  return (
    <div className="h-full flex relative">
      {foldersOpen ? <FolderRail view={filters.view} folder={filters.folder} onSelect={(id) => patch({ folder: id, view: viewForFolder(id), q: '' })} /> : null}
      {narrow && folderSheet ? (
        <Sheet title="Mappen" height="full" onClose={() => setFolderSheet(false)}>
          <FolderRail embedded view={filters.view} folder={filters.folder} onSelect={(id) => { patch({ folder: id, view: viewForFolder(id), q: '' }); setFolderSheet(false); }} />
        </Sheet>
      ) : null}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Op de telefoon staat boven een open mail de lezerkop; de filterbalk hoort bij de lijst. */}
        {narrow && selectedId ? null : <FilterBar filters={filters} onChange={(p) => patch({ ...p, ...(p.view && p.view !== filters.view ? { selectedId: null } : {}) })} onToggleFolders={() => (narrow ? setFolderSheet(true) : patch({ foldersOpen: !foldersOpen }))} foldersOpen={narrow ? folderSheet : foldersOpen} viewMenu={narrow ? undefined : viewMenuItems({ listVisible, mailOpen: Boolean(selectedId), readerPosition: choices.readerPosition }, { setListVisible, setReaderPosition: (position) => setMailChoices({ readerPosition: position }) })} />}
        <MailLayout narrow={narrow} mailOpen={Boolean(selectedId)} below={below} collapsed={collapsed} unread={unread} onShowList={() => setListVisible(true)} list={listPane} reader={readerPane} />
      </div>
      {menu && menuTarget ? (narrow ? <ActionSheet items={sheetItems} onClose={() => setMenu(null)} /> : <ContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} />) : null}
      {props.voice ? (
        <Suspense fallback={null}>
          {/* De sessie begint pas als de lijst geladen is (of het laden mislukte): bij een koude start staat de overlay er eerder dan de mails. */}
          <VoiceOverlay items={visible} ready={list.loadedAt > 0 || Boolean(list.error)} selectedId={selectedId} onSelect={select} onInstruct={(text) => submit(text, 'voice')} onExit={props.onVoiceEnd} filterLabel={filters.view === 'flagged' ? 'Gevlagd' : filters.view === 'unread' ? 'Ongelezen' : filters.view === 'processed' ? 'Verwerkt' : filters.view === 'search' ? 'Zoekresultaat' : 'Map'} />
        </Suspense>
      ) : null}
    </div>
  );
}
